import {
  DMCampaignType,
  DMCampaignAddResult,
  DMCampaignDeleteResult,
} from "@pollyslack/hub/src/types";
import {
  SequenceScheduleResult,
  SequenceRemoveResult,
} from "@pollyslack/hub/src/services/dmCampaign/dmCampaignService";
import { get, post } from "./rest";

const campaignsRoot = "/campaigns/";

// Re-export for consumers of this module
export type { SequenceScheduleResult, SequenceRemoveResult };

/** One time ("HH:mm") or a window, in each recipient's timezone (PS-5765) */
export interface SendScheduleParams {
  sendTime?: string;
  sendWindow?: { start: string; end: string };
}

// Sequence types
export interface SequenceInfo {
  id: string;
  dmCount: number;
  arms: ("A" | "B")[];
}

export async function addDMCampaign(
  userIds: string[],
  campaignType: DMCampaignType | string,
  sendDate?: string,
  bypassPaidPlanCheck?: boolean,
  sendSchedule?: SendScheduleParams,
): Promise<DMCampaignAddResult> {
  return await post(`${campaignsRoot}add`, {
    userIds,
    campaignType,
    sendDate,
    bypassPaidPlanCheck,
    ...sendSchedule,
  });
}

export async function removeDMCampaign(
  userIds: string[],
  campaignType: DMCampaignType | string,
): Promise<{
  totalProcessed: number;
  removed: number;
  skipped: number;
  notFound: number;
  notFoundUserIds: string[];
}> {
  return await post(`${campaignsRoot}remove`, { userIds, campaignType });
}

export async function optOutUsers(userIds: string[]): Promise<{
  totalProcessed: number;
  optedOut: number;
  notFound: number;
  notFoundUserIds: string[];
}> {
  return await post(`${campaignsRoot}opt-out`, { userIds });
}

export async function deleteDMCampaignMessages(
  userIds: string[],
  campaignType: DMCampaignType | string,
): Promise<DMCampaignDeleteResult> {
  return await post(`${campaignsRoot}delete-messages`, {
    userIds,
    campaignType,
  });
}

/**
 * Get available sequences
 */
export async function getSequences(): Promise<{ sequences: SequenceInfo[] }> {
  return await get(`${campaignsRoot}sequences`);
}

/**
 * Schedule all DMs in a sequence for multiple users
 */
export async function scheduleSequence(
  params: {
    userIds: string[];
    sequenceId: string;
    arm: "A" | "B";
    startDate: string;
    cadenceDays: number;
  } & SendScheduleParams,
): Promise<SequenceScheduleResult> {
  return await post(`${campaignsRoot}schedule-sequence`, params);
}

/**
 * Remove all DMs in a sequence for multiple users
 */
export async function removeSequence(params: {
  userIds: string[];
  sequenceId: string;
  arm: "A" | "B";
}): Promise<SequenceRemoveResult> {
  return await post(`${campaignsRoot}remove-sequence`, params);
}
